import { useApolloClient, useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AssetCard,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CurrencyAmountUI,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  PrimaryFullScreenSpinner,
  Spinner,
  StatusUI,
  Txt,
  toast,
} from "@repo/ui";
import { NotFoundError } from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, X } from "lucide-react";
import { Suspense, useCallback, useState } from "react";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { OrganisationCard } from "~/components/Organisation/OrganisationCard";
import { graphql } from "~/gql/client";
import type { AdminScheduledFundMovementDetailFragment } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/scheduled-fund-movements_/$scheduledFundMovementId",
)({
  head: () => opsHead("Scheduled Fund Movement"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

graphql(`
  fragment AdminScheduledFundMovementDetail on ScheduledFundMovement {
    id
    shortRef
    status
    amount {
      amount
      currency
    }
    requestedProcessingDate
    isFx
    isCrossEntity
    organisation {
      id
      ...OrganisationCardFields
    }
    ... on ScheduledTransfer {
      side
      sourceAsset {
        id
        name
        ...AssetCardFields
      }
      destinationAsset {
        id
        name
        ...AssetCardFields
      }
    }
    ... on ScheduledWithdrawal {
      beneficiaryAccount {
        id
        displayName
        ...BeneficiaryAssetCardFields
      }
    }
  }
`);

function kindLabel(
  scheduledFundMovement: AdminScheduledFundMovementDetailFragment,
): string {
  if (scheduledFundMovement.__typename === "ScheduledTransfer") {
    return "Scheduled Transfer";
  }
  if (scheduledFundMovement.__typename === "ScheduledWithdrawal") {
    return scheduledFundMovement.beneficiaryAccount?.kind === "PAYEE"
      ? "Scheduled Payment"
      : "Scheduled Withdrawal";
  }
  return "Scheduled Fund Movement";
}

function View() {
  const { scheduledFundMovementId } = Route.useParams();

  const { data, refetch } = useSuspenseQuery(
    graphql(`
      query AdminScheduledFundMovementDetailPage($id: ID!) {
        node(id: $id) {
          ... on ScheduledFundMovement {
            ...AdminScheduledFundMovementDetail
          }
        }
      }
    `),
    { variables: { id: scheduledFundMovementId } },
  );

  const scheduledFundMovement =
    data?.node?.__typename === "ScheduledTransfer" ||
    data?.node?.__typename === "ScheduledWithdrawal"
      ? data.node
      : undefined;

  useOpsTitle(
    scheduledFundMovement
      ? `${kindLabel(scheduledFundMovement)} #${scheduledFundMovement.shortRef}`
      : undefined,
  );

  if (!scheduledFundMovement) {
    throw new NotFoundError();
  }

  return (
    <Detail scheduledFundMovement={scheduledFundMovement} onRefetch={refetch} />
  );
}

function Detail({
  scheduledFundMovement,
  onRefetch,
}: {
  scheduledFundMovement: AdminScheduledFundMovementDetailFragment;
  onRefetch: () => void;
}) {
  const label = kindLabel(scheduledFundMovement);
  const showCancel = scheduledFundMovement.status === "SCHEDULED";

  return (
    <AdminPageContainer>
      <AdminPageHeader
        title={`${label} #${scheduledFundMovement.shortRef}`}
        subtitle={<div className="font-mono">{scheduledFundMovement.id}</div>}
        buttons={
          showCancel ? (
            <CancelButton
              scheduledFundMovementId={scheduledFundMovement.id}
              kindLabel={label}
              onCompleted={onRefetch}
            />
          ) : null
        }
      />

      <div className="flex flex-col gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Details</CardTitle>
          </CardHeader>
          <CardContent>
            <DetailRow
              label="Status"
              value={<StatusUI status={scheduledFundMovement.status} />}
            />
            <DetailRow
              label="Amount"
              value={
                scheduledFundMovement.amount ? (
                  <CurrencyAmountUI
                    amount={scheduledFundMovement.amount.amount}
                    currency={scheduledFundMovement.amount.currency}
                  />
                ) : (
                  "—"
                )
              }
            />
            <DetailRow
              label="Processing date"
              value={scheduledFundMovement.requestedProcessingDate ?? "—"}
            />
            <DetailRow
              label="Organisation"
              value={
                scheduledFundMovement.organisation ? (
                  <OrganisationCard
                    organisation={scheduledFundMovement.organisation}
                    linkTo={`/organisations/${scheduledFundMovement.organisation.id}`}
                  />
                ) : (
                  "—"
                )
              }
            />
            <DetailRow
              label="Cross entity"
              value={scheduledFundMovement.isCrossEntity ? "Yes" : "No"}
            />
            <DetailRow
              label="FX"
              value={scheduledFundMovement.isFx ? "Yes" : "No"}
            />
            {scheduledFundMovement.__typename === "ScheduledTransfer" && (
              <DetailRow label="Side" value={scheduledFundMovement.side} />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Route</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 py-4">
              <div className="flex-1">
                <Txt variant="detail-label">From</Txt>
                <div className="mt-2">
                  {scheduledFundMovement.__typename === "ScheduledTransfer" &&
                  scheduledFundMovement.sourceAsset ? (
                    <Link
                      to="/assets/$assetId"
                      params={{
                        assetId: scheduledFundMovement.sourceAsset.id,
                      }}
                    >
                      <AssetCard
                        asset={scheduledFundMovement.sourceAsset}
                        subText="BUSINESS_ENTITY"
                      />
                    </Link>
                  ) : (
                    <Txt variant="body">—</Txt>
                  )}
                </div>
              </div>
              <ArrowRight className="size-4 shrink-0 text-tertiary" />
              <div className="flex-1">
                <Txt variant="detail-label">To</Txt>
                <div className="mt-2">
                  {scheduledFundMovement.__typename === "ScheduledTransfer" &&
                  scheduledFundMovement.destinationAsset ? (
                    <Link
                      to="/assets/$assetId"
                      params={{
                        assetId: scheduledFundMovement.destinationAsset.id,
                      }}
                    >
                      <AssetCard
                        asset={scheduledFundMovement.destinationAsset}
                        subText="BUSINESS_ENTITY"
                      />
                    </Link>
                  ) : null}
                  {scheduledFundMovement.__typename === "ScheduledWithdrawal" &&
                  scheduledFundMovement.beneficiaryAccount ? (
                    <Link
                      to="/beneficiary-accounts/$beneficiaryAccountId"
                      params={{
                        beneficiaryAccountId:
                          scheduledFundMovement.beneficiaryAccount.id,
                      }}
                    >
                      <AssetCard
                        beneficiaryAccount={
                          scheduledFundMovement.beneficiaryAccount
                        }
                      />
                    </Link>
                  ) : null}
                  {((scheduledFundMovement.__typename === "ScheduledTransfer" &&
                    !scheduledFundMovement.destinationAsset) ||
                    (scheduledFundMovement.__typename ===
                      "ScheduledWithdrawal" &&
                      !scheduledFundMovement.beneficiaryAccount)) && (
                    <Txt variant="body">—</Txt>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminPageContainer>
  );
}

function CancelButton({
  scheduledFundMovementId,
  kindLabel,
  onCompleted,
}: {
  scheduledFundMovementId: string;
  kindLabel: string;
  onCompleted: () => void;
}) {
  const kindLower = kindLabel.toLowerCase();
  const client = useApolloClient();
  const [open, setOpen] = useState(false);

  const [setStatus, { loading }] = useMutation(
    graphql(`
      mutation AdminScheduledFundMovement_Cancel(
        $input: SetScheduledFundMovementStatusInput!
      ) {
        setScheduledFundMovementStatus(input: $input) {
          scheduledFundMovement {
            id
            status
          }
        }
      }
    `),
  );

  const handleConfirm = useCallback(async () => {
    await setStatus({
      variables: {
        input: { id: scheduledFundMovementId, status: "CANCELLED" },
      },
      onCompleted: () => {
        toast({
          kind: "success",
          autoHide: true,
          title: `${kindLabel} cancelled`,
        });
        setOpen(false);
        client.cache.evict({
          id: `ScheduledTransfer:${scheduledFundMovementId}`,
        });
        client.cache.evict({
          id: `ScheduledWithdrawal:${scheduledFundMovementId}`,
        });
        client.cache.gc();
        onCompleted();
      },
      onError: (error) => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Failed to cancel",
          description:
            error.graphQLErrors?.[0]?.message ??
            "An unexpected error occurred.",
        });
      },
    });
  }, [scheduledFundMovementId, setStatus, client, onCompleted, kindLabel]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive" size="sm" className="text-danger">
          <X className="size-4" />
          Cancel
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Cancel {kindLower}</DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <Txt>
            Are you sure you want to cancel this {kindLower}? This cannot be
            undone.
          </Txt>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <DialogClose asChild>
            <Button variant="secondary">Keep scheduled</Button>
          </DialogClose>
          <Button
            variant="destructive"
            disabled={loading}
            onClick={handleConfirm}
          >
            {loading ? <Spinner /> : `Cancel ${kindLower}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
